import React, { useState } from 'react';
import { Search, Package, Truck, CheckCircle, Clock, Phone, MapPin, ArrowLeft, MessageCircle } from 'lucide-react';
import api from '../lib/api';
import { resolveImageUrl } from '../lib/images';
import { formatPrice } from '../lib/format';

const OrderTracking = ({ onBack }) => {
  const [orderNumber, setOrderNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleTrack = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await api.post('/customer/track-order', {
        orderNumber: orderNumber.trim(),
        phone: phone.trim()
      });

      if (res.data.success) {
        setOrder(res.data.data);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to find order. Please check your details.');
    } finally {
      setLoading(false);
    }
  };

  const getWhatsAppLink = () => {
    const message = encodeURIComponent(
      `Hi! I need help with my OWLFIT order.\n\nOrder Number: ${order?.orderNumber}\nPhone: ${phone}`
    );
    return `https://wa.me/917054083677?text=${message}`;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-black text-white p-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-white/10 rounded">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-black tracking-tighter">TRACK YOUR ORDER</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {!order ? (
          // Search Form
          <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6 md:p-8">
            <div className="text-center mb-8">
              <Package size={64} className="mx-auto mb-4 text-gray-400" />
              <h2 className="text-3xl font-black mb-2">WHERE'S MY ORDER?</h2>
              <p className="text-gray-600">Enter your order number and phone number to track your package</p>
            </div>

            <form onSubmit={handleTrack} className="space-y-4 max-w-md mx-auto">
              <div>
                <label className="block font-bold text-sm mb-2">ORDER NUMBER</label>
                <input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
                  placeholder="OWL-202501-XXXX"
                  className="w-full p-4 border-2 border-black font-mono text-lg uppercase"
                  required
                />
              </div>

              <div>
                <label className="block font-bold text-sm mb-2">PHONE NUMBER</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit mobile number"
                  className="w-full p-4 border-2 border-black font-mono text-lg"
                  maxLength={10}
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border-2 border-red-500 p-4 text-red-700 font-bold">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-yellow-400 text-black py-4 font-black text-lg border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  'SEARCHING...'
                ) : (
                  <>
                    <Search size={24} />
                    TRACK ORDER
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 pt-8 border-t-2 border-gray-200 text-center">
              <p className="text-gray-600 mb-4">Can't find your order?</p>
              <a
                href="https://wa.me/917054083677?text=Hi!%20I%20need%20help%20tracking%20my%20OWLFIT%20order"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-500 text-white px-6 py-3 font-bold border-2 border-black"
              >
                <MessageCircle size={20} />
                Chat on WhatsApp
              </a>
            </div>
          </div>
        ) : (
          // Order Details
          <div className="space-y-6">
            {/* Order Header */}
            <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
              <div className="flex justify-between items-start flex-wrap gap-4">
                <div>
                  <div className="text-sm text-gray-500 mb-1">ORDER NUMBER</div>
                  <div className="text-2xl font-black">{order.orderNumber}</div>
                </div>
                <div className="text-right">
                  <div className={`inline-block px-4 py-2 font-bold text-sm uppercase border-2 border-black ${
                    order.status === 'delivered' ? 'bg-green-400' :
                    order.status === 'shipped' ? 'bg-blue-400' :
                    order.status === 'processing' ? 'bg-yellow-400' :
                    'bg-gray-200'
                  }`}>
                    {order.status}
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
              <h3 className="font-black text-xl mb-6">ORDER STATUS</h3>
              
              <div className="relative">
                {order.timeline.map((step, idx) => (
                  <div key={step.status} className="flex gap-4 mb-8 last:mb-0">
                    <div className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full border-4 flex items-center justify-center ${
                        step.completed 
                          ? 'bg-green-500 border-green-600 text-white' 
                          : 'bg-gray-100 border-gray-300 text-gray-400'
                      }`}>
                        {step.status === 'placed' && <CheckCircle size={20} />}
                        {step.status === 'processing' && <Clock size={20} />}
                        {step.status === 'shipped' && <Truck size={20} />}
                        {step.status === 'delivered' && <Package size={20} />}
                      </div>
                      {idx < order.timeline.length - 1 && (
                        <div className={`w-1 h-12 ${step.completed ? 'bg-green-500' : 'bg-gray-200'}`} />
                      )}
                    </div>
                    <div className="flex-1 pt-1">
                      <div className={`font-bold ${step.completed ? 'text-black' : 'text-gray-400'}`}>
                        {step.label}
                      </div>
                      {step.date && (
                        <div className="text-sm text-gray-500">
                          {new Date(step.date).toLocaleDateString('en-IN', {
                            weekday: 'short',
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Tracking Info */}
              {order.tracking?.trackingNumber && (
                <div className="mt-6 p-4 bg-gray-50 border-2 border-black">
                  <div className="font-bold mb-2">TRACKING NUMBER</div>
                  <div className="font-mono text-lg">{order.tracking.trackingNumber}</div>
                  {order.tracking.carrier && (
                    <div className="text-sm text-gray-600 mt-1">Carrier: {order.tracking.carrier}</div>
                  )}
                  {order.tracking.trackingUrl && (
                    <a
                      href={order.tracking.trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-blue-600 underline font-bold"
                    >
                      Track on carrier website →
                    </a>
                  )}
                </div>
              )}

              {/* Estimated Delivery */}
              {order.status !== 'delivered' && order.estimatedDelivery && (
                <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-400">
                  <div className="font-bold">ESTIMATED DELIVERY</div>
                  <div className="text-lg">
                    {new Date(order.estimatedDelivery).toLocaleDateString('en-IN', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long'
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
              <h3 className="font-black text-xl mb-4">ORDER ITEMS</h3>
              
              <div className="space-y-4">
                {order.items.map((item, idx) => (
                  <div key={idx} className="flex gap-4 p-4 border-2 border-gray-200">
                    <div className="w-20 h-20 bg-gray-100 border-2 border-black flex items-center justify-center">
                      {item.product?.images?.front?.url ? (
                        <img 
                          src={resolveImageUrl(item.product.images.front.url)}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package size={32} className="text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="font-bold">{item.name}</div>
                      <div className="text-sm text-gray-600">
                        Size: {item.size} | Qty: {item.quantity}
                      </div>
                      <div className="font-bold mt-1">{formatPrice(item.price)}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t-2 border-black">
                <div className="flex justify-between mb-2">
                  <span>Subtotal</span>
                  <span className="font-bold">{formatPrice(order.pricing.subtotal)}</span>
                </div>
                {order.pricing.shippingCharge > 0 && (
                  <div className="flex justify-between mb-2">
                    <span>Shipping</span>
                    <span className="font-bold">{formatPrice(order.pricing.shippingCharge)}</span>
                  </div>
                )}
                {order.pricing.codCharge > 0 && (
                  <div className="flex justify-between mb-2">
                    <span>COD Charge</span>
                    <span className="font-bold">{formatPrice(order.pricing.codCharge)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-black pt-2 border-t border-gray-300">
                  <span>TOTAL</span>
                  <span>{formatPrice(order.pricing.total)}</span>
                </div>
              </div>
            </div>

            {/* Shipping Address */}
            <div className="bg-white border-4 border-black shadow-[8px_8px_0px_black] p-6">
              <h3 className="font-black text-xl mb-4 flex items-center gap-2">
                <MapPin size={24} />
                DELIVERY ADDRESS
              </h3>
              <p className="font-bold">{order.shippingAddress.name}</p>
              <p className="text-gray-600">
                {order.shippingAddress.city}, {order.shippingAddress.state}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => setOrder(null)}
                className="flex-1 bg-white text-black py-4 font-bold border-4 border-black hover:bg-gray-100"
              >
                Track Another Order
              </button>
              <a
                href={getWhatsAppLink()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-green-500 text-white py-4 font-bold border-4 border-black flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} />
                Need Help?
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
